import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorReportModal from "../components/ErrorReportModal";
import ErrorPage from "../pages/ErrorPage";
import { useCreateErrorReport } from "../api/mutations";
import { useNotification } from "../hooks/useNotification";
import { useAuth } from "../contexts/AuthContext";

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public statusText: string,
    public url: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  public toJson(): Record<string, unknown> {
    return {
      message: this.message,
      statusCode: this.statusCode.toString(),
      statusText: this.statusText,
      url: this.url,
      timestamp: new Date().toISOString(),
    };
  }
}

const errorMessages: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  409: "Conflict",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

interface ErrorDisplayProps {
  error: APIError;
  messageSource?: "default" | "api";
  renderText?: boolean;
  errorHandledCallback?: () => void;
  autoReport?: boolean;
  redirect?: boolean;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  messageSource = "default",
  renderText = true,
  errorHandledCallback,
  autoReport = true,
  redirect = true,
}) => {
  const statusCode = error instanceof APIError ? error.statusCode : 500;
  const isExpectedClientError = [401, 403, 404].includes(statusCode);
  const [isModalOpen, setIsModalOpen] = useState(true);

  if (isExpectedClientError) {
    return <ErrorPage />;
  }
  const { notify } = useNotification();
  const navigate = useNavigate();

  const { isAuthenticated: isAuth } = useAuth();

  const createErrorReportMutation = useCreateErrorReport({
    onSuccess: async () => {
      notify("success", "Error reported!");
    },
    onError: () => {
      notify("error", "Failed to report error!");
    },
  });

  let statusMessage: string;
  if (messageSource === "default") {
    statusMessage = errorMessages[statusCode] || "An error occurred";
  } else if (messageSource === "api") {
    statusMessage = error?.message || "An error occurred";
  }

  const handleModalSubmit = async (description: string) => {
    // Create error report with user's description
    if (autoReport) {
      const errorData = error.toJson();
      createErrorReportMutation.mutate({
        message: errorData.message as string,
        statusCode: parseInt(errorData.statusCode as string),
        statusText: errorData.statusText as string,
        url: errorData.url as string,
        timestamp: errorData.timestamp as string,
        pageUrl: window.location.href,
        userDescription: description.trim() || undefined,
      });
    }
    setIsModalOpen(false);
    if (redirect) {
      if (!isAuth) {
        navigate("/login", { replace: true });
      } else {
        navigate("/explore", { replace: true });
      }
    }
    errorHandledCallback?.();
  };

  const handleModalClose = () => {
    // Create error report without description when user skips
    if (autoReport) {
      const errorData = error.toJson();
      createErrorReportMutation.mutate({
        message: errorData.message as string,
        statusCode: parseInt(errorData.statusCode as string),
        statusText: errorData.statusText as string,
        url: errorData.url as string,
        timestamp: errorData.timestamp as string,
        pageUrl: window.location.href,
      });
    }
    setIsModalOpen(false);
    if (redirect) {
      if (!isAuth) {
        navigate("/login", { replace: true });
      } else {
        navigate("/explore", { replace: true });
      }
    }
    errorHandledCallback?.();
  };

  return (
    <div>
      {renderText && <div>{statusMessage}</div>}
      <ErrorReportModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};
